import * as React from 'react';
import {
  weeklyDatePickerDriverFactory,
  WeeklyDatePickerDriver,
} from '../WeeklyDatePicker.driver';
import {
  TPAComponentsProvider,
  TPAComponentsConfig,
} from '../../TPAComponentsConfig';
import { WeeklyDatePicker, WeeklyDatePickerProps } from '../index';
import { getWeekDatesRange } from '../../WeeklyDateNavigation';
import { addDays, addWeeks } from 'date-fns';
import { createRendererWithUniDriver } from '../../../test/utils';
import { formatDateForDataAttr } from '../util/dateUtils';

describe('WeeklyDatePicker', () => {
  const dataHook = 'compDataHook';
  const render = createRendererWithUniDriver<WeeklyDatePickerDriver>(
    weeklyDatePickerDriverFactory,
  );

  const bootstrap = (
    props: Partial<WeeklyDatePickerProps> = {},
    contextProps: TPAComponentsConfig = {},
  ) => {
    const compProps = {
      'data-hook': dataHook,
      onChange: () => {},
      value: new Date(),
      ...props,
    };
    return render(
      <TPAComponentsProvider value={contextProps}>
        <WeeklyDatePicker {...compProps} />
      </TPAComponentsProvider>,
      dataHook,
    );
  };

  beforeAll(() => {
    jest.useFakeTimers('modern').setSystemTime(new Date(2022, 5, 14));
  });

  afterAll(() => {
    jest.useRealTimers();
  });

  it('should display the correct selected date', async () => {
    const date = new Date(2022, 5, 14);
    const { driver } = bootstrap({ value: date });

    expect(await driver.getSelectedDate()).toBe('14');
    expect(await driver.getCaptionText()).toBe('June 2022');
  });

  it('should call onChange with the clicked day', async () => {
    const onChange = jest.fn();
    const date = new Date(2022, 5, 14);
    const { driver } = bootstrap({ value: date, onChange });

    expect(onChange).toHaveBeenCalledTimes(0);

    await driver.clickDay(new Date(2022, 5, 15));

    expect(onChange).toHaveBeenCalledTimes(1);
    expect(onChange.mock.calls[0][0].getDate()).toEqual(15);
  });

  it('should call onDisplayedWeekChange with the first day of the week', async () => {
    const date = new Date(2022, 5, 14);
    const expectedWeek = getWeekDatesRange(addWeeks(date, 1));
    const onDisplayedWeekChange = jest.fn();

    const { driver } = bootstrap({ value: date, onDisplayedWeekChange });

    expect(onDisplayedWeekChange).toHaveBeenCalledTimes(0);

    await driver.clickOnNextWeekButton();

    expect(onDisplayedWeekChange).toHaveBeenCalledTimes(1);
    const onDisplayWeekChangeValue = onDisplayedWeekChange.mock.calls[0][0];
    expect(onDisplayWeekChangeValue).toEqual(expectedWeek);
  });

  it('should render the tab index of the first available date as 0 when the date is in the middle of the week and the week starts with a disabled date', async () => {
    jest.useFakeTimers('modern').setSystemTime(new Date(2022, 6, 13));
    const { driver } = bootstrap({
      value: new Date(2022, 6, 15),
      excludePastDates: true,
    });

    expect((await driver.getDatesByTabIndex(0)).at(0)).toBe('13');
    expect(await driver.getDatesByTabIndex(-1)).toEqual([
      '11',
      '12',
      '14',
      '15',
      '16',
      '17',
    ]);
  });

  describe('keyboard navigation', () => {
    it('should move focus to the next day when right arrow is pressed', async () => {
      const date = new Date();
      const { driver } = bootstrap({
        value: date,
      });

      await driver.clickDay(date);
      await driver.pressRightArrowKey();

      const expectedDate = addDays(date, 1);
      const focusedDate = await driver.getFocusedDate();
      expect(focusedDate).toEqual(formatDateForDataAttr(expectedDate));
    });

    it('should keep focus on the same day when left arrow is pressed and previous day is disabled', async () => {
      const date = new Date();

      const { driver } = bootstrap({
        value: date,
        excludePastDates: true,
      });

      await driver.clickDay(date);
      await driver.pressLeftArrowKey();

      const focusedDate = await driver.getFocusedDate();

      expect(focusedDate).toEqual(formatDateForDataAttr(date));
    });

    it('should move to next week when the focus on day last day on week and click on arrow right on keyboard', async () => {
      const date = new Date(2022, 5, 14);
      const currentWeek = getWeekDatesRange(date);
      const onDisplayedWeekChange = jest.fn();

      const { driver } = bootstrap({ value: date, onDisplayedWeekChange });

      expect(onDisplayedWeekChange).toHaveBeenCalledTimes(0);
      await driver.clickDay(currentWeek.endOfWeek);
      await driver.pressRightArrowKey();

      expect(onDisplayedWeekChange).toHaveBeenCalledTimes(1);
      const onDisplayWeekChangeValue = onDisplayedWeekChange.mock.calls[0][0];
      expect(onDisplayWeekChangeValue.startOfWeek.toLocaleString()).toEqual(
        addWeeks(currentWeek.startOfWeek, 1).toLocaleString(),
      );
      expect(onDisplayWeekChangeValue.endOfWeek.toLocaleString()).toEqual(
        addWeeks(currentWeek.endOfWeek, 1).toLocaleString(),
      );
    });

    it('should move to next week and focus the next day when right arrow is pressed on the last day of the week', async () => {
      const date = new Date();

      const lastDay = getWeekDatesRange(date, 1).endOfWeek;

      const { driver } = bootstrap({
        value: lastDay,
        excludePastDates: false,
      });

      await driver.clickDay(lastDay);
      await driver.pressRightArrowKey();

      const expectedDate = addDays(lastDay, 1);
      const focusedDate = await driver.getFocusedDate();
      expect(focusedDate).toEqual(formatDateForDataAttr(expectedDate));
    });
  });

  describe('arrowLocation prop', () => {
    it('should render arrows next to dates when arrowLocation is "dates"', async () => {
      const { driver } = bootstrap({ arrowLocation: 'dates' });
      const nextArrow = await driver.getNextWeekArrowButton();
      expect(await nextArrow.exists()).toBe(true);
    });

    it('should render arrows next to caption when arrowLocation is "caption"', async () => {
      const { driver } = bootstrap({ arrowLocation: 'caption' });
      const nextArrow = await driver.getNextWeekArrowButton();
      expect(await nextArrow.exists()).toBe(true);
    });

    it('should render the caption element with the correct dataHook ', async () => {
      const { driver } = bootstrap({ arrowLocation: 'dates' });
      const caption = driver.getCaptionElement();
      expect(await caption.attr('data-hook')).toBe(`${dataHook}-caption`);
    });
  });
});
